import styled, { css } from 'styled-components';
import Icon from 'src/components/Icon';

export const Page = styled.div(
  ({ theme }) => css`
    min-height: 100%;
    background: ${theme.co.primary98};
    color: ${theme.co.secondary10};
    display: flex;
    flex-direction: column;
  `,
);

export const Header = styled.header<{ $dark: boolean }>(
  ({ theme, $dark }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.sz.s4};
    padding: ${theme.sz.s4} ${theme.sz.s6};
    color: ${theme.co.secondary40};

    ${
      $dark &&
      css`
        background: ${theme.co.secondary10};
        color: ${theme.co.primary99};
      `
    }

    @media (max-width: ${theme.sz.lg}) {
      background: transparent;
      color: ${theme.co.secondary40};
      padding: ${theme.sz.s4};
    }
  `,
);

export const BackButton = styled.button(
  ({ theme }) => css`
    display: none;
    align-items: center;
    justify-content: center;
    width: ${theme.sz.s10};
    height: ${theme.sz.s10};
    padding: 0;
    border: 0;
    background: transparent;
    color: ${theme.co.secondary10};
    font-size: ${theme.sz.s6};
    cursor: pointer;

    @media (max-width: ${theme.sz.lg}) {
      display: inline-flex;
    }
  `,
);

export const Logo = styled(Icon)<{ $hideOnMobile?: boolean }>(
  ({ theme, $hideOnMobile }) => css`
    color: ${theme.co.primary20};
    font-size: ${theme.sz.s10};

    ${
      $hideOnMobile &&
      css`
        @media (max-width: ${theme.sz.lg}) {
          display: none;
        }
      `
    }
  `,
);

export const Streak = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s2};
    margin-left: auto;
    ${theme.typography.paragraph.base};
    font-weight: ${theme.DEPRECATED_ty.bold};
  `,
);

export const Body = styled.div(
  ({ theme }) => css`
    width: 100%;
    max-width: ${theme.sz.xl};
    margin: 0 auto;
    padding: ${theme.sz.s8} ${theme.sz.s8} ${theme.sz.s10};
    box-sizing: border-box;

    @media (max-width: ${theme.sz.lg}) {
      padding: ${theme.sz.s4} ${theme.sz.s4} ${theme.sz.s8};
    }
  `,
);

export const Prompt = styled.div(
  ({ theme }) => css`
    max-width: ${theme.sz.ms};
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  `,
);

export const MascotWrap = styled.div(
  ({ theme }) => css`
    width: ${theme.sz.s24};
    height: ${theme.sz.s24};
    margin-bottom: ${theme.sz.s5};

    svg {
      width: 100%;
      height: 100%;
    }
  `,
);

export const SuccessPill = styled.p(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    gap: ${theme.sz.s2};
    margin: 0 0 ${theme.sz.s6};
    padding: ${theme.sz.s2} ${theme.sz.s4};
    border-radius: ${theme.bo.round};
    background: ${theme.co.success98};
    color: ${theme.co.success20};
    ${theme.typography.paragraph.base};
  `,
);

export const Remember = styled.p(
  ({ theme }) => css`
    margin: 0;
    ${theme.typography.heading.desktop.l};
    color: ${theme.co.secondary10};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.l};
    }
  `,
);

export const Percent = styled.p(
  ({ theme }) => css`
    margin: 0;
    ${theme.typography.heading.desktop.accent.xxl};
    font-size: 4.5rem;
    line-height: 1;
    color: ${theme.co.secondary10};

    @media (max-width: ${theme.sz.lg}) {
      font-size: 3.5rem;
    }
  `,
);

export const Better = styled.p(
  ({ theme }) => css`
    margin: 0 0 ${theme.sz.s4};
    ${theme.typography.heading.desktop.l};
    color: ${theme.co.secondary10};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.l};
    }
  `,
);

export const Recall = styled.p(
  ({ theme }) => css`
    margin: 0 0 ${theme.sz.s6};
    max-width: 36rem;
    ${theme.typography.paragraph.l};
    color: ${theme.co.secondary40};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.paragraph.base};
    }
  `,
);

export const Question = styled.p(
  ({ theme }) => css`
    margin: 0;
    max-width: 28rem;
    ${theme.typography.paragraph.l};
    color: ${theme.co.secondary40};
  `,
);

export const PromptActions = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: ${theme.sz.s3};
    width: 100%;
    margin-top: ${theme.sz.s8};

    button {
      min-width: 16rem;
    }

    @media (max-width: ${theme.sz.lg}) {
      flex-direction: column-reverse;

      button {
        width: 100%;
        min-width: 0;
      }
    }
  `,
);

export const ScheduleLayout = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(18rem, 0.85fr);
    gap: ${theme.sz.s8};
    align-items: start;

    @media (max-width: ${theme.sz.lg}) {
      grid-template-columns: minmax(0, 1fr);
      gap: ${theme.sz.s5};
    }
  `,
);

export const Title = styled.h1(
  ({ theme }) => css`
    margin: 0 0 ${theme.sz.s3};
    ${theme.typography.heading.desktop.l};
    color: ${theme.co.secondary10};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.m};
    }
  `,
);

export const Subtitle = styled.p<{ $desktopOnly?: boolean }>(
  ({ theme, $desktopOnly }) => css`
    margin: 0 0 ${theme.sz.s6};
    ${theme.typography.paragraph.base};
    color: ${theme.co.secondary50};

    ${
      $desktopOnly
        ? css`
            @media (max-width: ${theme.sz.lg}) {
              display: none;
            }
          `
        : css`
            display: none;

            @media (max-width: ${theme.sz.lg}) {
              display: block;
            }
          `
    }
  `,
);

export const Options = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s3};
  `,
);

export const Option = styled.label<{ $selected: boolean }>(
  ({ theme, $selected }) => css`
    position: relative;
    display: flex;
    align-items: center;
    gap: ${theme.sz.s3};
    padding: ${theme.sz.s4};
    border-radius: ${theme.bo.medium};
    border: ${theme.bo.mediumWidth} solid ${$selected ? theme.co.primary20 : theme.co.secondary95};
    background: ${theme.co.neutral99};
    cursor: pointer;
    text-align: left;

    &:focus-within {
      outline: ${theme.bo.mediumWidth} solid ${theme.co.secondary10};
      outline-offset: ${theme.sz.s05};
    }
  `,
);

export const Radio = styled.input`
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  margin: 0;
`;

export const RadioMark = styled.span<{ $selected: boolean }>(
  ({ theme, $selected }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: ${theme.sz.s6};
    height: ${theme.sz.s6};
    border-radius: ${theme.bo.elliptical};
    border: ${theme.bo.mediumWidth} solid ${$selected ? theme.co.primary20 : theme.co.secondary80};
    background: ${$selected ? theme.co.primary20 : theme.co.neutral99};
    color: ${theme.co.secondary10};
    font-size: ${theme.sz.s3};
  `,
);

export const OptionText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const OptionTitle = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xxs};
    color: ${theme.co.secondary10};
  `,
);

export const OptionNote = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.secondary50};
  `,
);

export const OptionTime = styled.span(
  ({ theme }) => css`
    margin-left: auto;
    flex: 0 0 auto;
    ${theme.typography.paragraph.base};
    color: ${theme.co.secondary40};
    white-space: nowrap;
  `,
);

export const TimeInput = styled.input(
  ({ theme }) => css`
    margin-left: auto;
    border: 0;
    background: transparent;
    ${theme.typography.paragraph.base};
    color: ${theme.co.secondary10};
    cursor: pointer;
  `,
);

export const PlanCard = styled.div(
  ({ theme }) => css`
    padding: ${theme.sz.s5};
    border-radius: ${theme.bo.mediumLarge};
    background: ${theme.co.primary99};
    border: ${theme.bo.thinWidth} solid ${theme.co.primary90};
  `,
);

export const Eyebrow = styled.p(
  ({ theme }) => css`
    margin: 0 0 ${theme.sz.s3};
    ${theme.typography.paragraph.s};
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${theme.co.secondary50};

    @media (max-width: ${theme.sz.lg}) {
      display: none;
    }
  `,
);

export const Quote = styled.p(
  ({ theme }) => css`
    margin: 0 0 ${theme.sz.s4};
    ${theme.typography.heading.desktop.s};
    color: ${theme.co.secondary10};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.xs};
    }
  `,
);

export const MonthLine = styled.p(
  ({ theme }) => css`
    display: flex;
    align-items: flex-start;
    gap: ${theme.sz.s2};
    margin: 0 0 ${theme.sz.s4};
    ${theme.typography.paragraph.base};
    color: ${theme.co.secondary40};
  `,
);

export const MonthIcon = styled(Icon)(
  ({ theme }) => css`
    flex: 0 0 auto;
    margin-top: ${theme.sz.s05};
    color: ${theme.co.secondary50};
    font-size: ${theme.sz.s5};
  `,
);

export const EventPreview = styled.div(
  ({ theme }) => css`
    margin-bottom: ${theme.sz.s4};
    padding: ${theme.sz.s3} ${theme.sz.s4};
    border-radius: ${theme.bo.mediumSmall};
    border-left: ${theme.bo.thickWidth} solid ${theme.co.primary20};
    background: ${theme.co.neutral99};

    @media (max-width: ${theme.sz.lg}) {
      display: none;
    }
  `,
);

export const EventTitle = styled.p(
  ({ theme }) => css`
    margin: 0;
    ${theme.typography.heading.desktop.xxs};
    color: ${theme.co.secondary10};
  `,
);

export const EventSchedule = styled.p(
  ({ theme }) => css`
    margin: ${theme.sz.s1} 0 0;
    ${theme.typography.paragraph.s};
    color: ${theme.co.secondary50};
  `,
);

export const Providers = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.sz.s2};
  `,
);

export const Provider = styled.label<{ $selected: boolean }>(
  ({ theme, $selected }) => css`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: ${theme.sz.s16};
    padding: ${theme.sz.s2} ${theme.sz.s4};
    border-radius: ${theme.bo.round};
    border: ${theme.bo.thinWidth} solid ${$selected ? theme.co.primary70 : theme.co.secondary90};
    background: ${$selected ? theme.co.primary90 : theme.co.neutral99};
    color: ${theme.co.secondary10};
    ${theme.typography.paragraph.base};
    cursor: pointer;

    &:focus-within {
      outline: ${theme.bo.mediumWidth} solid ${theme.co.secondary10};
      outline-offset: ${theme.sz.s05};
    }
  `,
);

export const Actions = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.sz.s4};
    margin-top: ${theme.sz.s4};

    button {
      width: 100%;
    }
  `,
);

export const SkipButton = styled.button(
  ({ theme }) => css`
    border: 0;
    background: transparent;
    padding: ${theme.sz.s2};
    cursor: pointer;
    ${theme.typography.heading.desktop.xxs};
    color: ${theme.co.secondary10};
  `,
);
